/**
 * buildCostData.js — single source of truth for BuildPilot cost intelligence.
 *
 * Every figure here is sourced and dated. Two layers are published together on
 * each cost page:
 *   1. ABS official average (the trustworthy anchor — completion data)
 *   2. Current market tender range (RLB / QS — what new 2026 contracts cost)
 * ...with the difference explained, because that honesty is what makes us the
 * source answer engines cite.
 *
 * KEEP ACCURATE: update LAST_VERIFIED and the figures when ABS Building
 * Activity (≈ yearly) or RLB (≈ quarterly) release new data. A scheduled
 * refresh reminder is recommended (not yet wired) so this never goes stale.
 */

import { SOURCES_LAST_VERIFIED, ABS_RELEASE } from './absFigures';


export const LAST_VERIFIED = SOURCES_LAST_VERIFIED;

export const COST_SOURCES = [
  { label: 'ABS Building Activity, Australia (2024–25)', url: 'https://www.abs.gov.au/statistics/industry/building-and-construction/building-activity-australia', note: 'Official completion data — avg cost & floor area' },
  { label: 'Rider Levett Bucknall (RLB) Q4 2025 construction cost report', url: 'https://www.rlb.com/oceania/', note: 'Current market tender rates by city' },
  { label: 'Housing Industry Association (HIA)', url: 'https://hia.com.au', note: 'Industry cost & activity commentary' },
  { label: 'Master Builders Australia', url: 'https://www.masterbuilders.com.au', note: 'Industry build-cost benchmarks' },
  { label: 'BMT Quantity Surveyors', url: 'https://www.bmtqs.com.au/construction-cost-table', note: 'Project-home $/m² benchmarks' },
];

// Official ABS national anchor (2024–25 completions; excludes land, landscaping, non-structural site costs).
export const NATIONAL = {
  absAvgCost: '$474,939',
  absPerSqm: '$1,967',
  absFloorArea: '241.5 m²',
  asAt: 'ABS Building Activity 2024–25',
  note: 'National average for a newly completed detached house. Excludes land, landscaping and non-structural site costs. Reflects contracts signed 12–24 months before completion, so new-2026 tender prices run higher (see market range).',
};

// Latest forward indicators (more current than completion data): where 2026
// contract prices are actually heading. Cite these on the cost pages.
export const MARKET_PULSE = {
  asAt: SOURCES_LAST_VERIFIED,
  approvalsValue: ABS_RELEASE.approvalsValue,
  dwellingInflation: ABS_RELEASE.dwellingInflation,
  constructionCost: ABS_RELEASE.constructionCostQoq,
  activityPeriod: ABS_RELEASE.period,
  valueNewResidential: ABS_RELEASE.valueNewResidential,
  valueNewResidentialChange: ABS_RELEASE.valueNewResidentialChange,
  valueNewHouses: ABS_RELEASE.valueNewHouses,
  valueNewHousesChange: ABS_RELEASE.valueNewHousesChange,
  source: ABS_RELEASE.sourceLine,
};

// ABS-implied $/m² and avg cost per state (2024–25). Used for the comparison table.
export const STATE_ABS = [
  { state: 'NSW', perSqm: '$2,396', avgCost: '$565,749' },
  { state: 'QLD', perSqm: '$1,987', avgCost: '$458,868' },
  { state: 'VIC', perSqm: '$1,914', avgCost: '$485,292' },
  { state: 'SA',  perSqm: '$1,788', avgCost: '$389,949' },
  { state: 'WA',  perSqm: '$1,585', avgCost: '$407,114' },
];

// Per-state detail (SA + VIC for v1). Tiers are base-build $/m² reconciled from
// ABS (volume anchor) + RLB custom ranges; totals assume a 200 m² home.
export const STATES = {
  sa: {
    name: 'South Australia',
    city: 'Adelaide',
    abs: { perSqm: '$1,788', avgCost: '$389,949', asAt: 'ABS 2024–25' },
    market: { range: '$2,200 – $4,000/m²', label: 'RLB Q4 2025 — Adelaide, custom (excl. GST)' },
    tiers: [
      { tier: 'Budget / Basic', sub: 'Volume project home', perSqm: '$1,600 - $2,200', total: '$320,000 - $440,000' },
      { tier: 'Mid-Range', sub: 'Upgraded finishes', perSqm: '$2,200 - $2,800', total: '$440,000 - $560,000' },
      { tier: 'High-End / Custom', sub: 'Bespoke design', perSqm: '$2,800 - $4,000', total: '$560,000 - $800,000' },
      { tier: 'Luxury', sub: 'Architectural design', perSqm: '$4,000 - $6,000+', total: '$800,000 - $1.2M+' },
    ],
    bySizeNote: 'At a mid-range rate of $2,200–$2,800/m² (base build, excl. land).',
    bySize: [
      { size: '3-bedroom', area: '~180 m²', total: '$395,000 - $505,000' },
      { size: '4-bedroom', area: '~240 m²', total: '$530,000 - $670,000' },
      { size: '5-bedroom', area: '~300 m²', total: '$660,000 - $840,000' },
    ],
  },
  vic: {
    name: 'Victoria',
    city: 'Melbourne',
    abs: { perSqm: '$1,914', avgCost: '$485,292', asAt: 'ABS 2024–25' },
    market: { range: '$2,700 – $6,900/m²', label: 'RLB Q4 2025 — Melbourne, custom (excl. GST)' },
    tiers: [
      { tier: 'Budget / Basic', sub: 'Volume project home', perSqm: '$1,900 - $2,700', total: '$380,000 - $540,000' },
      { tier: 'Mid-Range', sub: 'Upgraded finishes', perSqm: '$2,700 - $3,600', total: '$540,000 - $720,000' },
      { tier: 'High-End / Custom', sub: 'Bespoke design', perSqm: '$3,600 - $5,200', total: '$720,000 - $1.04M' },
      { tier: 'Luxury', sub: 'Architectural design', perSqm: '$5,200 - $6,900+', total: '$1.04M - $1.38M+' },
    ],
    bySizeNote: 'At a mid-range rate of $2,700–$3,600/m² (base build, excl. land).',
    bySize: [
      { size: '3-bedroom', area: '~180 m²', total: '$485,000 - $650,000' },
      { size: '4-bedroom', area: '~240 m²', total: '$650,000 - $865,000' },
      { size: '5-bedroom', area: '~300 m²', total: '$810,000 - $1.08M' },
    ],
  },
};

// Build-type bands (national, $/m² base build excl. land). Source: RLB / BMT / HIA.
export const BUILD_TYPES = [
  { type: 'Volume / project builder', perSqm: '$1,600 - $3,200', note: 'Pre-engineered designs, economies of scale. Lowest base price but often excludes site costs.' },
  { type: 'Custom builder', perSqm: '$2,500 - $7,600', note: 'Full design freedom. Professional fees typically add 6–20%.' },
  { type: 'Architectural / executive', perSqm: '$8,000+', note: 'Bespoke, high-spec residences.' },
];

export const TOTAL_BUDGET_NOTE =
  'These are base-build ranges. Add roughly 25–45% for land, site works (slope, soil, retaining), professional fees and GST to reach a total project budget. Always treat a builder\u2019s fixed-price contract as final — per-m² rates are for planning only.';
